import { type AppHost, AppMainView } from '#packages/apilib';
import {
  EditorAPIKey,
  EditorCoreApiKey,
  EditorParamsApiKey,
} from '#packages/apis';
import fontPickerEnTranslations from '@wix/font-picker/dist/cjs/assets/locale/messages_en.json';
import { DndProvider } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import ReactDOM from 'react-dom/client';
import _ from 'lodash';
import * as util from '#packages/util';
import { addResources, i18n } from '#packages/i18n';
import { fedopsLogger, editorWixRecorder } from '#packages/util';
import React from 'react';
import { I18nextProvider } from 'react-i18next';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider as StoreProvider } from 'react-redux';
import * as baseUI from '#packages/baseUI';
import { WixBaseUiEnvironmentProvider } from '@wix/wix-base-ui';
import { WixDesignSystemProvider } from '@wix/design-system';
import { FontPickerEssentialsProvider } from '@wix/font-picker';
import * as stateManagement from '#packages/stateManagement';
import experiment from 'experiment';
// Needed to fix cyclic dependency in Mobile App Builder,
// see #53249 for details
import '@/platform';
// eslint-disable-next-line @wix/santa-editor/scoped-imports
import { bootstrapNewAddPanel } from '@/addPanelInfra/bootstrap';
// eslint-disable-next-line @wix/santa-editor/scoped-imports
import bootstrapComponentModel from '@/componentModel/bootstrap';
import { createEditorAppHost } from './createEditorHost';
import { initDebugTools } from './debugTools';

import type { EditorPermissions } from '#packages/editorPermissions';
import type { EditorLoggers } from 'types/core';
import type { EditorParams } from '#packages/editorParams';
import type { EditorBaseHooks } from '#packages/editorCore';

async function initQATools(store: AnyFixMe, editorAPI: AnyFixMe) {
  if (!window.queryUtil.isParameterTrue('isqa')) {
    return;
  }
  const [qaAutomation, debugTools] = await Promise.all([
    import('./santa-dom-selectors'),
    import('@/debugTools'),
  ]);

  window.testApi = Object.assign(
    {},
    window.testApi,
    {
      domSelectors: qaAutomation.getDomSelectors(),
      helpers: debugTools.helpers,
      clientSelectors: debugTools.clientSelectors,
      publishState: {
        isPublishedInSession: false,
      },
      stateManagement,
    },
    debugTools.createAutomationApi(editorAPI),
    { editorAPI },
  );
  qaAutomation.init(window);
}

function createGlobalServices() {
  return {
    panelsWorkerService: {},
    tooltipManager: baseUI.tooltipManager,
    mediaManagerSdkFonts: {
      uploadQueue: [] as AnyFixMe,
    },
  };
}

async function build({
  editorParams,
  editorLoggers,
  editorPermissions,
  editorBaseHooks,
}: {
  editorParams: EditorParams;
  editorLoggers: EditorLoggers;
  editorPermissions: EditorPermissions;
  editorBaseHooks: EditorBaseHooks;
}) {
  util.imageTransform.init(); // Initiate imageClientAPI (imageTransform)

  if (experiment.isOpen('qualaroo')) {
    util.qualaroo.start();
  }

  const host = createEditorAppHost({
    editorParams,
    editorPermissions,
    editorBaseHooks,
    globalServices: createGlobalServices(),
    debugMode: editorParams.isDebug && !editorParams.isQA,
  });

  const editorAPI = host.getAPI(EditorAPIKey);
  const throttledStore = editorAPI.store;

  throttledStore.dispatch(
    stateManagement.services.actions.loadService('editorAPI', editorAPI) as any,
  );

  throttledStore.dispatch(
    stateManagement.services.actions.loadService('host', host) as any,
  );

  fedopsLogger.create(editorLoggers.fedops);

  host.getAPI(EditorCoreApiKey).hooks.initReady.promise.then(() => {
    util.browserUtil.setTabTitle(editorAPI.siteDisplayName.get());
  });

  const doubleStore = _.defaults(
    {
      getState() {
        const state = throttledStore.getState();
        return {
          state,
          dsRead: editorAPI.dsRead || {},
          editorAPI,
          host,
        };
      },
    },
    throttledStore,
  );

  window.addEventListener('focus', () => {
    throttledStore.dispatch(stateManagement.window.actions.onFocus());
  });

  await initQATools(throttledStore, editorAPI);

  if (editorParams.isDebug) {
    initDebugTools(editorAPI);
  }

  bootstrapComponentModel();
  bootstrapNewAddPanel({ editorAPI, editorParams });

  host.getAPI(EditorCoreApiKey).hooks.initReady.promise.then(() => {
    editorWixRecorder.forceRecordIfNeeded(editorParams);
  });

  const loadFontPickerTranslations = async (
    lang: string,
  ): Promise<Record<string, string>> => {
    const map = {
      cs: () =>
        import('@wix/font-picker/dist/cjs/assets/locale/messages_cs.json'),
      da: () =>
        import('@wix/font-picker/dist/cjs/assets/locale/messages_da.json'),
      de: () =>
        import('@wix/font-picker/dist/cjs/assets/locale/messages_de.json'),
      es: () =>
        import('@wix/font-picker/dist/cjs/assets/locale/messages_es.json'),
      fr: () =>
        import('@wix/font-picker/dist/cjs/assets/locale/messages_fr.json'),
      id: () =>
        import('@wix/font-picker/dist/cjs/assets/locale/messages_id.json'),
      it: () =>
        import('@wix/font-picker/dist/cjs/assets/locale/messages_it.json'),
      ja: () =>
        import('@wix/font-picker/dist/cjs/assets/locale/messages_ja.json'),
      ko: () =>
        import('@wix/font-picker/dist/cjs/assets/locale/messages_ko.json'),
      nl: () =>
        import('@wix/font-picker/dist/cjs/assets/locale/messages_nl.json'),
      no: () =>
        import('@wix/font-picker/dist/cjs/assets/locale/messages_no.json'),
      pl: () =>
        import('@wix/font-picker/dist/cjs/assets/locale/messages_pl.json'),
      pt: () =>
        import('@wix/font-picker/dist/cjs/assets/locale/messages_pt.json'),
      ru: () =>
        import('@wix/font-picker/dist/cjs/assets/locale/messages_ru.json'),
      sv: () =>
        import('@wix/font-picker/dist/cjs/assets/locale/messages_sv.json'),
      th: () =>
        import('@wix/font-picker/dist/cjs/assets/locale/messages_th.json'),
      tr: () =>
        import('@wix/font-picker/dist/cjs/assets/locale/messages_tr.json'),
      uk: () =>
        import('@wix/font-picker/dist/cjs/assets/locale/messages_uk.json'),
      vi: () =>
        import('@wix/font-picker/dist/cjs/assets/locale/messages_vi.json'),
      zh: () =>
        import('@wix/font-picker/dist/cjs/assets/locale/messages_zh.json'),
    };

    const loadTranslations = Object.hasOwn(map, lang)
      ? map[lang as keyof typeof map]
      : null;

    if (loadTranslations) {
      return (await loadTranslations()).default as Record<string, string>;
    }

    throw new Error(`Font picker doesn't have translations for: ${lang}`);
  };

  const lang = i18n.language;
  try {
    if (lang === 'en') {
      addResources(fontPickerEnTranslations);
    } else {
      const translations = await loadFontPickerTranslations(lang);

      addResources(translations);
    }
  } catch (e) {
    console.error(`Failed to add translations(${lang}) for font picker`, e);
    addResources(fontPickerEnTranslations);
  }

  return { host, editorAPI, store: doubleStore, stateManagement };
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false } },
});

function render({
  host,
  store,
}: {
  host: AppHost;
  store: stateManagement.EditorStore;
}): { unmount: () => void } {
  editorWixRecorder.start();
  const editorParamsApi = host.getAPI(EditorParamsApiKey);

  const rootAppContainer = window.document.getElementById('rootApp');

  const rootContainer =
    rootAppContainer || window.document.getElementById('root');

  if (util.workspace.isNewWorkspaceEnabled()) {
    window.document.documentElement.classList.add('classic-facelift-skin');
  }

  fedopsLogger.appLoadingPhaseStart('editor-visible');
  fedopsLogger.appLoadingPhaseStart('preview-frame-mounted');

  const rootElement = (
    <QueryClientProvider client={queryClient}>
      <StoreProvider store={store}>
        <I18nextProvider i18n={i18n}>
          {/* @ts-expect-error */}
          <DndProvider backend={HTML5Backend}>
            <WixBaseUiEnvironmentProvider
              madefor
              theme="facelift"
              inputBorders={true}
              {...editorParamsApi.baseUIOverrides}
            >
              <WixDesignSystemProvider features={{ newColorsBranding: true }}>
                <FontPickerEssentialsProvider essentials={{ i18n }}>
                  <AppMainView host={host} />
                </FontPickerEssentialsProvider>
              </WixDesignSystemProvider>
            </WixBaseUiEnvironmentProvider>
          </DndProvider>
        </I18nextProvider>
      </StoreProvider>
    </QueryClientProvider>
  );

  const root = ReactDOM.createRoot(rootContainer);
  window.rendered = root.render(rootElement);

  return {
    unmount: () => root.unmount(),
  };
}

export { render, build, createEditorAppHost };
